/**
 * Authenticated app chrome (sidebar + topbar). Shared by the agency dashboard
 * and platform admin. `body` is the inner content HTML for the active page.
 */

import { logoHtml, esc } from './brand.js';
import { icon } from './icons.js';
import { isEmbedded } from './embed.js';

export interface NavItem {
  href: string;
  label: string;
  ico: string;
  key: string;
}

export interface ShellOpts {
  brandName: string;
  sub: string;
  nav: NavItem[];
  activeKey: string;
  title: string;
  headAction?: string;
  userEmail: string;
  userRole: string;
  body: string;
  /** Optional banner HTML rendered above the topbar (e.g. impersonation notice). */
  banner?: string;
  /** Optional uploaded logo URL; falls back to a monogram when absent. */
  logoUrl?: string;
  /** Apex-owned chrome (e.g. the platform console): renders the brand-kit
   *  wordmark instead of an agency logo/monogram, and scopes the shell to the
   *  brand-kit monochrome palette so no agency's white-label colors leak in. */
  apex?: boolean;
}

export function shell(o: ShellOpts): string {
  const nav = o.nav
    .map(
      (n) =>
        `<a class="navlink ${n.key === o.activeKey ? 'active' : ''}" href="${n.href}" data-link><span class="ico">${n.ico}</span> ${esc(n.label)}</a>`,
    )
    .join('');
  const initials = (o.userEmail[0] || 'U').toUpperCase();
  // Inside the GHL frame the host page already supplies a header and its own
  // branding, and the frame's vertical space is what GHL leaves over. The brand
  // row is the one piece of chrome that is purely duplicated, so it goes; the
  // nav stays because GHL's sidebar cannot navigate within this app.
  const embedded = isEmbedded();
  const brandRow = embedded
    ? ''
    : `<div class="brandrow">${logoHtml(o.brandName, { sub: o.sub, logoUrl: o.logoUrl, apex: o.apex })}</div>`;
  return `
  <div class="shell${o.apex ? ' apex-chrome' : ''}${embedded ? ' embedded' : ''}">
    <aside class="sidebar">
      ${brandRow}
      ${nav}
      <div class="sb-foot row" style="gap:10px">
        <span class="avatar">${initials}</span>
        <div style="font-size:13px;line-height:1.2;flex:1;min-width:0">
          <strong style="display:block;overflow:hidden;text-overflow:ellipsis">${esc(o.userEmail)}</strong>
          <span class="muted">${esc(o.userRole)}</span>
        </div>
        <button class="btn ghost sm" data-action="logout" title="Sign out">${icon('logout')}</button>
      </div>
    </aside>
    <div class="main">
      ${o.banner ?? ''}
      <div class="topbar"><h1>${esc(o.title)}</h1>${o.headAction ?? ''}</div>
      <div class="content">${o.body}</div>
    </div>
  </div>`;
}

/** A simple centered loading / error state. */
export function centered(message: string): string {
  return `<div class="center-screen"><div class="muted">${esc(message)}</div></div>`;
}

/** Centered loading spinner. */
export function loading(): string {
  return `<div class="loading-wrap"><div class="spinner"></div></div>`;
}
